import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

export default function ProductAutocomplete({
  value,
  onChange,
  options = [],
  placeholder = "Pesquise por código ou descrição",
  disabled = false,
  menuFixo = false,
  onSearch,
  searchLoading = false,
  selectedOption = null,
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [posicao, setPosicao] = useState(null);
  const [selecionadoLocal, setSelecionadoLocal] = useState(null);
  const ref = useRef(null);
  const botao = useRef(null);
  const selecionado =
    options.find((opcao) => opcao.value === value) ||
    (selectedOption?.value === value ? selectedOption : null) ||
    (selecionadoLocal?.value === value ? selecionadoLocal : null);
  const termo = busca.trim();
  const filtradas =
    termo.length >= 3
      ? onSearch
        ? options
        : options.filter((opcao) =>
            opcao.label
              .toLocaleLowerCase("pt-BR")
              .includes(termo.toLocaleLowerCase("pt-BR")),
          )
      : [];

  useEffect(() => {
    const fechar = (evento) => {
      if (!ref.current?.contains(evento.target)) setAberto(false);
    };
    document.addEventListener("mousedown", fechar);
    return () => document.removeEventListener("mousedown", fechar);
  }, []);

  useEffect(() => {
    if (!aberto || !menuFixo) return undefined;
    const atualizar = () => {
      const retangulo = botao.current?.getBoundingClientRect();
      if (retangulo) {
        setPosicao({
          top: retangulo.bottom + 4,
          left: retangulo.left,
          width: Math.max(retangulo.width, 288),
        });
      }
    };
    atualizar();
    window.addEventListener("resize", atualizar);
    window.addEventListener("scroll", atualizar, true);
    return () => {
      window.removeEventListener("resize", atualizar);
      window.removeEventListener("scroll", atualizar, true);
    };
  }, [aberto, menuFixo]);

  useEffect(() => {
    if (!value) setSelecionadoLocal(null);
  }, [value]);

  const alterarBusca = (valor) => {
    setBusca(valor);
    onSearch?.(valor);
  };

  const escolher = (opcao) => {
    onChange(opcao.value);
    setSelecionadoLocal(opcao);
    setBusca("");
    onSearch?.("");
    setAberto(false);
  };

  return (
    <div ref={ref} className="relative mt-1">
      <button
        ref={botao}
        type="button"
        disabled={disabled}
        onClick={() => setAberto((valor) => !valor)}
        className="flex w-full items-center justify-between rounded border border-gray-300 bg-white p-2 text-left disabled:bg-gray-100"
      >
        <span className={selecionado ? "" : "text-gray-500"}>
          {selecionado?.label || placeholder}
        </span>
        <ChevronDown
          size={18}
          className={`transition-transform ${aberto ? "rotate-180" : ""}`}
        />
      </button>
      {aberto && (
        <div
          className={`${menuFixo ? "fixed" : "absolute mt-1 w-full"} z-30 overflow-hidden rounded border border-gray-200 bg-white shadow-lg`}
          style={menuFixo && posicao ? posicao : undefined}
        >
          <label className="relative block border-b border-gray-200">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input
              autoFocus
              value={busca}
              onChange={(evento) => alterarBusca(evento.target.value)}
              className="w-full py-2 pl-9 pr-8 outline-none"
              placeholder="Digite pelo menos 3 letras"
            />
            {busca && (
              <button
                type="button"
                onClick={() => alterarBusca("")}
                className="absolute right-2 top-2 text-gray-500"
                aria-label="Limpar busca"
              >
                <X size={16} />
              </button>
            )}
          </label>
          <div className="max-h-56 overflow-y-auto">
            {termo.length < 3 ? (
              <p className="p-3 text-sm text-gray-500">
                Digite pelo menos 3 letras para localizar produtos.
              </p>
            ) : searchLoading ? (
              <p className="p-3 text-sm text-gray-500">Buscando produtos...</p>
            ) : filtradas.length ? (
              filtradas.map((opcao) => (
                <button
                  type="button"
                  key={opcao.value}
                  onClick={() => escolher(opcao)}
                  className={`block w-full px-3 py-2 text-left hover:bg-blue-50 ${opcao.value === value ? "bg-blue-50 text-blue-700" : ""}`}
                >
                  {opcao.label}
                </button>
              ))
            ) : (
              <p className="p-3 text-sm text-gray-500">
                Nenhum produto encontrado.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
